import { Controller } from "@hotwired/stimulus"

// Panel propio (ya NO es el offcanvas de Bootstrap): controlamos open/close, el
// slide (clase .is-open) y, en mobile, el fullscreen + teclado iOS (visualViewport
// → --vvh) + lock real del body. Sin esto último Bootstrap nos peleaba el viewport.
const MOBILE = "(max-width: 767.98px)"
const MIN_WIDTH = 320
const MAX_WIDTH = 640
const MIN_CONTENT = 400 // espacio mínimo que dejamos para el contenido al lado del panel

export default class extends Controller {
  static targets = ["form", "messages", "scroll", "textarea", "pathname", "empty", "newConversation"]

  // Una vez que arrancó la conversación nos quedamos en modo "has-messages"
  // aunque #support_messages quede momentáneamente vacío (entre el submit y el
  // broadcast del primer mensaje). Se resetea con "Nueva conversación".
  conversationStarted = false
  isOpen = false

  // Empuja el contenido en vez de taparlo. La clase va en <html> (no <body>)
  // porque Turbo reemplaza el <body> al navegar pero no el <html>, así el push
  // se mantiene al cambiar de vista con el panel abierto.
  pushOpen = () => document.documentElement.classList.add("support-open")
  pushClose = () => document.documentElement.classList.remove("support-open")

  connect() {
    this.updateEmpty()
    this.observer = new MutationObserver(() => {
      this.updateEmpty()
      this.scrollToBottom()
    })
    this.observer.observe(this.messagesTarget, { childList: true, subtree: true })

    // El botón que abre el panel vive en el nav (fuera de este controller). Lo
    // wireamos por id; en cada navegación Turbo lo re-crea, por eso lo buscamos
    // de nuevo acá (y lo soltamos en disconnect).
    this.launchButton = document.getElementById("supportToggle")
    this.launchButton?.addEventListener("click", this.toggle)
    document.addEventListener("keydown", this.onEscape)
    document.addEventListener("turbo:before-stream-render", this.beforeStreamRender)
    window.addEventListener("resize", this.reclampWidth)

    this.restoreWidth()

    // Continuidad con turbo-permanent: si el panel ya estaba abierto antes de
    // navegar, la clase `support-open` (en <html>) y `.is-open` (en el <aside>,
    // que se mueve con sus clases) sobreviven, pero la instancia del controller
    // es nueva → re-armamos el estado JS y, si es mobile, re-aplicamos lock/teclado.
    if (this.element.classList.contains("is-open") || document.documentElement.classList.contains("support-open")) {
      this.isOpen = true
      this.element.classList.add("is-open")
      this.element.setAttribute("aria-hidden", "false")
      this.launchButton?.setAttribute("aria-expanded", "true")
      this.pushOpen()
      if (this.isMobile()) { this.lockBodyScroll(); this.attachViewport() }
    }

    // Al navegar, Turbo re-inserta este elemento permanente y el contenedor
    // scrolleable se resetea arriba. Fijamos al fondo sincrónicamente (antes del
    // primer paint) para que no se vea el salto de inicio→fondo.
    this.pinToBottom()
    this.scrollToBottom() // backup tras layout (ya sin animación)
  }

  disconnect() {
    this.observer?.disconnect()
    this.launchButton?.removeEventListener("click", this.toggle)
    document.removeEventListener("keydown", this.onEscape)
    document.removeEventListener("turbo:before-stream-render", this.beforeStreamRender)
    window.removeEventListener("resize", this.reclampWidth)
    this.detachViewport()
    if (this.resizing) this.resizeEnd()
    // OJO: no soltamos el lock ni `support-open` acá. disconnect dispara en CADA
    // navegación con el panel abierto; el estado vive en <html>/el elemento y se
    // re-arma en el próximo connect. (El body viejo se descarta de todos modos.)
  }

  /* ----------------------------- Open / close ----------------------------- */
  toggle = () => { this.isOpen ? this.close() : this.open() }

  open() {
    this.isOpen = true
    this.element.classList.add("is-open")
    this.element.setAttribute("aria-hidden", "false")
    this.launchButton?.setAttribute("aria-expanded", "true")
    this.pushOpen()

    if (this.isMobile()) {
      this.lockBodyScroll()
      this.attachViewport()
    } else if (this.hasTextareaTarget) {
      // En mobile NO enfocamos: dispararía el teclado y un salto de layout.
      this.textareaTarget.focus()
    }
    this.scrollToBottom()
  }

  close() {
    this.isOpen = false
    this.element.classList.remove("is-open")
    this.element.setAttribute("aria-hidden", "true")
    this.launchButton?.setAttribute("aria-expanded", "false")
    this.pushClose()

    this.unlockBodyScroll()
    this.detachViewport()
    this.launchButton?.focus()
  }

  onEscape = (e) => {
    if (this.isOpen && e.key === "Escape") this.close()
  }

  /* ----------------------- Mobile: teclado + lock ------------------------- */
  isMobile() {
    return window.matchMedia(MOBILE).matches
  }

  // --vvh = alto realmente visible (descuenta el teclado). --vv-top = cuánto se
  // corrió el área visible hacia abajo (offsetTop). En iOS el teclado NO mueve los
  // elementos `fixed` (quedan en el layout viewport), pero el área visible se
  // desplaza; sin seguir offsetTop, el panel queda arriba del área visible (el
  // header se va por arriba y la tienda asoma abajo del input).
  updateVvh = () => {
    const vv = window.visualViewport
    const h = vv?.height || window.innerHeight
    const top = vv?.offsetTop || 0
    const root = document.documentElement.style
    root.setProperty("--vvh", `${h}px`)
    root.setProperty("--vv-top", `${top}px`)
  }

  attachViewport() {
    if (this.vvAttached || !window.visualViewport) return
    this.vvAttached = true
    this.updateVvh()
    window.visualViewport.addEventListener("resize", this.updateVvh)
    window.visualViewport.addEventListener("scroll", this.updateVvh)
  }

  detachViewport() {
    if (!this.vvAttached) return
    this.vvAttached = false
    window.visualViewport?.removeEventListener("resize", this.updateVvh)
    window.visualViewport?.removeEventListener("scroll", this.updateVvh)
    document.documentElement.style.removeProperty("--vvh")
    document.documentElement.style.removeProperty("--vv-top")
  }

  // Lock real de iOS: position:fixed + top negativo (overflow:hidden solo no basta).
  lockBodyScroll() {
    if (this.bodyLocked) return
    this.bodyLocked = true
    this.savedScrollY = window.scrollY
    const s = document.body.style
    s.position = "fixed"
    s.top = `-${this.savedScrollY}px`
    s.left = "0"
    s.right = "0"
    s.width = "100%"
  }

  unlockBodyScroll() {
    if (!this.bodyLocked) return
    this.bodyLocked = false
    const s = document.body.style
    s.position = ""
    s.top = ""
    s.left = ""
    s.right = ""
    s.width = ""
    window.scrollTo(0, this.savedScrollY || 0)
  }

  /* --------------------------- Ancho (resize) ----------------------------- */
  // --support-width vive en <html>: lo leen el ancho del panel, el push de desktop
  // y el offset del sticky-nav, así todo se mueve junto.
  restoreWidth() {
    const saved = parseInt(localStorage.getItem("supportWidth"), 10)
    if (!saved) return
    document.documentElement.style.setProperty("--support-width", `${this.clampWidth(saved)}px`)
  }

  // El máximo no es fijo: en pantallas chicas (tablet) lo limita el viewport para
  // que el contenido conserve un ancho usable (si no, el panel + el push se comen
  // todo y el contenido deja de empujarse / se desborda).
  clampWidth(w) {
    const max = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, window.innerWidth - MIN_CONTENT))
    return Math.min(max, Math.max(MIN_WIDTH, w))
  }

  // Si cambia el tamaño de la ventana (resize/rotación de tablet), re-clampeamos el
  // ancho guardado al nuevo viewport para que el contenido no quede aplastado.
  reclampWidth = () => {
    const current = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--support-width"), 10) || 350
    const clamped = this.clampWidth(current)
    if (clamped !== current) {
      document.documentElement.style.setProperty("--support-width", `${clamped}px`)
    }
  }

  // Drag del borde izquierdo (solo desktop; el handle está display:none en mobile).
  resizeStart(e) {
    if (this.isMobile()) return
    e.preventDefault()
    this.resizing = true
    this.startX = e.clientX
    this.startWidth = this.element.getBoundingClientRect().width
    this.lastWidth = this.startWidth
    // Cortamos transiciones (el push del body) y la selección de texto durante el drag.
    document.documentElement.classList.add("support-resizing")
    window.addEventListener("pointermove", this.resizeMove)
    window.addEventListener("pointerup", this.resizeEnd)
  }

  // El panel está a la derecha y crece hacia la IZQUIERDA: arrastrar a la izquierda
  // (dx negativo) lo agranda → width = startWidth - dx.
  resizeMove = (e) => {
    if (!this.resizing) return
    this.lastWidth = this.clampWidth(this.startWidth - (e.clientX - this.startX))
    document.documentElement.style.setProperty("--support-width", `${this.lastWidth}px`)
  }

  resizeEnd = () => {
    if (!this.resizing) return
    this.resizing = false
    document.documentElement.classList.remove("support-resizing")
    window.removeEventListener("pointermove", this.resizeMove)
    window.removeEventListener("pointerup", this.resizeEnd)
    localStorage.setItem("supportWidth", Math.round(this.lastWidth))
  }

  // Inyecta la vista actual antes de enviar; el server la mapea a la línea de contexto.
  beforeSubmit() {
    if (this.hasPathnameTarget) this.pathnameTarget.value = window.location.pathname

    // Flip optimista: en cuanto el partner manda texto, bajamos el input al fondo
    // (modo has-messages) sin esperar el broadcast, para que no haya salto.
    if (this.textareaTarget.value.trim().length > 0) {
      this.conversationStarted = true
      this.setMode(true)
    }
  }

  // Descarta una reescritura de streaming tardía. El stream usa `update` (reescribe
  // el contenido completo, idempotente), así que esto ya NO evita duplicados —no
  // pueden ocurrir—; solo impide que un `update` rezagado pise el markdown final
  // una vez que el `replace` del frame marcó la burbuja como completa.
  beforeStreamRender = (event) => {
    const stream = event.target
    if (stream?.getAttribute("action") !== "update") return

    const target = stream.getAttribute("target")
    if (!target?.startsWith("content_support_message_")) return

    const content = document.getElementById(target)
    if (content?.dataset.supportStreamComplete === "true") event.preventDefault()
  }

  // "Nueva conversación": el turbo_stream vacía #support_messages; reseteamos el
  // flag acá para que el observer, al ver 0 hijos, vuelva a centrar el input.
  resetConversation() {
    this.conversationStarted = false
  }

  afterSubmit(event) {
    if (event.detail?.success === false) return
    this.textareaTarget.value = ""
    this.autogrow()
    this.scrollToBottom()
  }

  onKeydown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      this.formTarget.requestSubmit()
    }
  }

  autogrow() {
    const el = this.textareaTarget
    el.style.height = "auto"
    el.style.height = `${el.scrollHeight}px`
  }

  // El partial solo renderiza bubbles user/assistant (un `system` no produce DOM),
  // así que children.length ES la misma noción de "vacío" que usa el guard del
  // server (SupportChat#has_conversation?). El botón y la empty-state leen ese
  // único estado: no inventamos otra definición de "vacío" en el cliente.
  updateEmpty() {
    const realCount = this.messagesTarget.children.length > 0
    if (realCount) this.conversationStarted = true
    const hasConversation = realCount || this.conversationStarted

    // El botón "Nueva conversación" lee el estado REAL del server (mensajes > 0),
    // no el flag optimista: no debe habilitarse antes de que exista el mensaje.
    if (this.hasNewConversationTarget) this.newConversationTarget.disabled = !realCount

    this.setMode(hasConversation)
  }

  // Centrado (no-messages) vs layout normal con input abajo (has-messages).
  setMode(hasConversation) {
    this.element.classList.toggle("has-messages", hasConversation)
    this.element.classList.toggle("no-messages", !hasConversation)
    if (this.hasEmptyTarget) this.emptyTarget.hidden = hasConversation
  }

  scrollToBottom() {
    if (!this.hasScrollTarget) return
    requestAnimationFrame(() => {
      this.scrollTarget.scrollTop = this.scrollTarget.scrollHeight
    })
  }

  // Fija al fondo de forma sincrónica (sin rAF, sin animación).
  pinToBottom() {
    if (!this.hasScrollTarget) return
    this.scrollTarget.scrollTop = this.scrollTarget.scrollHeight
  }
}
